import type { ReactElement, ReactNode } from 'react';
import { useRef } from 'react';
import type { AudienceSegment, DocumentSection, Proposal } from '@shared/types';
import type { PageActions } from '../types';
import { EditableMoney, EditableText } from '../../components/Editable';
import { count, money } from '../../lib/format';
import { artProductId, channel, tacticDeliveryMetrics, tacticFor } from '../../lib/plan';
import { useBindings } from '../../state/BindingsContext';
import { fitScaleStyle, useFitScale } from '../../hooks/useFitScale';
import { productDeviceAsset } from './assets';
import { StatTile, TagPill } from './primitives';
import { HeadphonesIcon, PeopleIcon, PlayIcon, TrendUpIcon, TvIcon } from './icons';

/**
 * product_plan `awareness_plan` variant (`docs/momentum-v3-slides.md`
 * product_plan; media-pairs-all-contract.md "Details slide: awareness_plan").
 * Renders for ctv / online_video / streaming_audio — one shared creative, no
 * lanes. Redesign round (slide feedback: "three narrow equal cards, mostly
 * empty"): the three cards are no longer equal-width siblings. Layout is
 * now [left column: WHERE IT RUNS stacked over WHO SEES IT, each half the
 * row height] [THE MESSAGE, wide, image-led] [metric rail, unchanged] — same
 * header/frame/rail chrome as `dual_path` (`ProductPlanDualPath.tsx`) and
 * `audience_plan` (`ProductPlanAudience.tsx`) so the three variants still
 * read as one family. Reuses the already-landed `.mv3-dualpath-*` /
 * `.mv3-planpath-*` classes from `momentum-v3-mediapair.css` (owned by a
 * concurrent stream — not edited here) for the header, card box, rail tiles
 * and callout, plus the family's own type-scale classes
 * (`.mv3-planpath-role` / `.mv3-planvariant-subline` / `.mv3-planpath-
 * eyebrow`) for THE MESSAGE's text stack; the new grid geometry and THE
 * MESSAGE's image slot are new `.mv3-planvariant-*` classes in this file's
 * own stylesheet (`momentum-v3-planvariants.css`).
 *
 * No `paths` on this variant (media-pairs-all-contract.md: "no `paths`").
 * Rail is impressions (or views for cpv) only — NEVER reach/frequency, per
 * the contract's explicit accuracy rule; `tacticDeliveryMetrics` has no such
 * fields to begin with, so there is nothing to accidentally render there.
 *
 * `content.body` / `fields.measurementNote` are never read here — the
 * landed agent contract (`src/agent/prompt.ts`) tells the agent not to set
 * either on this variant; any stale value from an older run is intentionally
 * ignored rather than surfaced.
 *
 * Fit-scale: only THE MESSAGE's own text stack (message/role/format, below
 * its fixed-height image slot) ever measures and shrinks — the real
 * ad6abc03 fixture renders it at scale 1 with the sizes below, so this is a
 * safety net for an unusually long message, not something the typical deck
 * relies on. WHERE IT RUNS / WHO SEES IT hold short, bounded content (at
 * most 4 placement chips, at most 3 audience chips, one geo line) that the
 * halved card height comfortably fits without ever needing to measure or
 * shrink — no `useFitScale` on either of them. The callout keeps its own
 * independent scale, same pattern as dual_path/audience_plan.
 */

/** "WHERE IT RUNS" heading icon by product (media-pairs-all-contract.md). */
function whereItRunsIcon(productId: string | undefined, size: number): ReactElement {
  if (productId === 'streaming_audio') return <HeadphonesIcon size={size} />;
  if (productId === 'online_video') return <PlayIcon size={size} />;
  return <TvIcon size={size} />;
}

function segmentNames(proposal: Proposal, ids: string[]): string[] {
  const byId = new Map(proposal.audience.segments.map((segment) => [segment.id, segment]));
  return ids
    .map((id) => byId.get(id))
    .filter((segment): segment is AudienceSegment => !!segment)
    .map((segment) => segment.name || segment.segment || '')
    .filter((name) => name.length > 0)
    .slice(0, 3);
}

/**
 * THE MESSAGE's image slot: `content.imageUrl` first, else the sibling
 * `media_<productId>_divider` section's own `content.imageUrl` (same
 * sibling-lookup shape `ChannelHero.tsx`'s `planContentSibling` uses, just
 * the other direction — a plan card borrowing its divider's hero image
 * instead of a divider borrowing its plan's copy), else null — the slot
 * then shows the channel's kit device art, contained, so it is never empty.
 */
function siblingDividerImageUrl(proposal: Proposal, productId: string): string | null {
  const sections = proposal.document?.sections ?? [];
  const sibling = sections.find(
    (candidate) => candidate.productId === productId && candidate.type === 'product_divider',
  );
  return sibling?.content.imageUrl || null;
}

function InfoCard({ title, icon, children }: { title: string; icon: ReactElement; children: ReactNode }): ReactElement {
  return (
    <div className="mv3-planpath-card mv3-planvariant-halfcard">
      <div className="mv3-planpath-card-inner">
        <div className="mv3-planpath-heading">
          <span className="mv3-planpath-icon">{icon}</span>
          <div className="mv3-planpath-heading-text">
            <div className="mv3-planvariant-cardtitle">{title}</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function AwarenessPlan({
  proposal,
  section,
  actions,
  readOnly,
  label,
}: {
  proposal: Proposal;
  section: DocumentSection;
  actions: PageActions;
  readOnly: boolean;
  label: string;
}): ReactElement {
  const resolve = useBindings();
  const productId = section.productId;
  const content = section.content;
  const line = productId ? channel(proposal, productId) : null;
  const metrics = productId ? tacticDeliveryMetrics(proposal, productId) : null;
  const tactic = productId ? tacticFor(proposal, productId) : null;
  const headline = content.headline || label;
  const callout = content.callout ?? null;
  const creative = content.creative ?? null;
  const budgetDisplay = line ? money(line.monthlyBudget) : null;

  const placements = (creative?.placements ?? []).slice(0, 4);
  const audienceNames = segmentNames(proposal, tactic?.audienceSegmentIds ?? []);
  const geoLine = proposal.geography.areas.map((area) => area.label).join(' · ');
  const artId = productId ? artProductId(proposal, productId) : undefined;
  const messageImageUrl = content.imageUrl || (productId ? siblingDividerImageUrl(proposal, productId) : null) || null;

  const calloutInnerRef = useRef<HTMLDivElement | null>(null);
  const calloutScale = useFitScale(calloutInnerRef, { min: 0.78 });

  // THE MESSAGE is the only card that ever measures/shrinks — see the
  // file-level doc comment for why WHERE IT RUNS / WHO SEES IT don't.
  const messageTextRef = useRef<HTMLDivElement | null>(null);
  const messageScale = useFitScale(messageTextRef, { min: 0.78 });

  const calloutIcon = <TrendUpIcon size={36} />;
  const calloutDefaultTitle = 'How it pays off';

  return (
    <div className="mv3-dualpath-frame">
      <div className="mv3-dualpath-header">
        <div className="mv3-eyebrow">{label}</div>
        <div className="mv3-dualpath-headline">
          <EditableText
            value={headline}
            field="headline"
            placeholder={label}
            readOnly={readOnly}
            variant="headline"
            onSave={(next) => actions.saveContent(section, { headline: next })}
          />
        </div>
        {productId && line ? (
          <div className="mv3-dualpath-budget">
            <span className="mv3-dualpath-budget-label">Monthly budget</span>
            <span className="mv3-dualpath-budget-value">
              <EditableMoney
                value={line.monthlyBudget}
                display={budgetDisplay ? `${budgetDisplay} / month` : null}
                readOnly={readOnly}
                label={`${label} monthly budget`}
                onSave={(next) => actions.setChannelBudget(productId, next, label)}
              />
            </span>
          </div>
        ) : null}
      </div>

      <div className="mv3-planvariant-awareness-grid">
        <div className="mv3-planvariant-leftcol">
          <InfoCard title="Where it runs" icon={whereItRunsIcon(artId, 40)}>
            {placements.length > 0 ? (
              <div className="mv3-planpath-block">
                <div className="mv3-planpath-chips">
                  {placements.map((placement) => (
                    <TagPill tone="ghost" key={placement}>
                      {resolve(placement)}
                    </TagPill>
                  ))}
                </div>
              </div>
            ) : null}
          </InfoCard>

          <InfoCard title="Who sees it" icon={<PeopleIcon size={40} />}>
            {audienceNames.length > 0 ? (
              <div className="mv3-planpath-block">
                <div className="mv3-planpath-chips">
                  {audienceNames.map((name) => (
                    <TagPill tone="ghost" key={name}>
                      {resolve(name)}
                    </TagPill>
                  ))}
                </div>
              </div>
            ) : null}
            {geoLine ? (
              <div className="mv3-planpath-block">
                <div className="mv3-planpath-eyebrow">Where</div>
                <div className="mv3-planvariant-line">{geoLine}</div>
              </div>
            ) : null}
          </InfoCard>
        </div>

        <div className="mv3-planpath-card mv3-planvariant-message-card">
          <div className="mv3-planvariant-message-inner">
            <div className="mv3-planvariant-message-image">
              {messageImageUrl ? (
                <img className="mv3-planvariant-message-image-img" src={messageImageUrl} alt="" />
              ) : (
                <img
                  className="mv3-planvariant-message-image-img mv3-planvariant-message-image-device"
                  src={productDeviceAsset(artId)}
                  alt=""
                />
              )}
            </div>
            <div className="mv3-planvariant-message-textbox">
              <div
                className="mv3-planvariant-message-textinner"
                ref={messageTextRef}
                style={fitScaleStyle(messageScale)}
              >
                {creative?.message ? (
                  <div className="mv3-planpath-role">
                    <EditableText
                      value={creative.message}
                      field="body"
                      placeholder="The on-screen line the ad carries"
                      readOnly={readOnly}
                      onSave={(next) => actions.saveContent(section, { creative: { ...creative, message: next } })}
                    />
                  </div>
                ) : null}
                {tactic?.role ? <div className="mv3-planvariant-subline">{resolve(tactic.role)}</div> : null}
                {creative?.format ? <div className="mv3-planpath-eyebrow">Format · {resolve(creative.format)}</div> : null}
              </div>
            </div>
          </div>
        </div>

        <div className="mv3-dualpath-rail">
          {metrics?.kind === 'cpv' ? (
            <StatTile className="mv3-dualpath-tile" label="Est. views / month" value={count(metrics.views)} />
          ) : (
            <StatTile className="mv3-dualpath-tile" label="Est. impressions / month" value={count(metrics?.impressions ?? null)} />
          )}
          {callout ? (
            <div className="mv3-dualpath-callout">
              <div className="mv3-dualpath-callout-inner" ref={calloutInnerRef} style={fitScaleStyle(calloutScale)}>
                <span className="mv3-dualpath-callout-icon">{calloutIcon}</span>
                <div className="mv3-dualpath-callout-title">{callout.title ? resolve(callout.title) : calloutDefaultTitle}</div>
                <div className="mv3-dualpath-callout-body">
                  <EditableText
                    value={callout.body}
                    field="body"
                    placeholder="Callout body"
                    readOnly={readOnly}
                    onSave={(next) => actions.saveContent(section, { callout: { ...callout, body: next } })}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
